import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Building, Phone, FileText,
    AlertCircle, Check, Loader, ArrowLeft,
    Package, Store, Award, Gift, Users, Plus, X,
    Wrench, GraduationCap, Trash2, Banknote, Home, ShoppingBag, MapPin
} from 'lucide-react';
import './SellerApplication.css';

function SellerApplication({ userInfo, onBack, initialVendorType }) {
    const [step, setStep] = useState(initialVendorType ? 1 : 0); // 0: 업종선택, 1: 기본정보, 2: 카테고리, 3: 거래처, 4: 소개
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [hasExistingApplication, setHasExistingApplication] = useState(false);
    const [newPartner, setNewPartner] = useState({ name: '', phone: '', email: '' });

    // 업종 타입 정의
    const vendorTypes = [
        { id: 'material_supplier', label: '재료마켓', icon: ShoppingBag, color: '#8b5cf6', desc: '치과/기공 재료 및 장비 판매' },
        { id: 'equipment_as', label: '장비 A/S', icon: Wrench, color: '#f59e0b', desc: '치과/기공 장비 수리 및 유지보수' },
        { id: 'education', label: '교육/세미나', icon: GraduationCap, color: '#10b981', desc: '치과/기공 관련 교육 및 세미나' },
        { id: 'cleaning', label: '청소/폐기물', icon: Trash2, color: '#6366f1', desc: '의료폐기물 처리 및 청소 서비스' },
        { id: 'finance', label: '금융', icon: Banknote, color: '#ec4899', desc: '치과 전문 대출, 리스, 보험' },
        { id: 'interior', label: '인테리어/개원', icon: Home, color: '#14b8a6', desc: '치과 인테리어 및 개원 컨설팅' },
    ];

    // 폼 데이터
    const [formData, setFormData] = useState({
        // 업종 타입
        vendorType: initialVendorType || '',

        // 기본 정보 (users에서 가져옴)
        companyName: userInfo?.businessName || '',
        businessNumber: userInfo?.businessNumber || '',
        ownerName: userInfo?.name || '',
        contactEmail: userInfo?.email || '',
        contactPhone: userInfo?.phone || '',
        address: userInfo?.address || '',
        detailAddress: '',

        // 공통 추가 정보
        businessType: '',
        productCategories: [],
        description: '',
        website: '',
        employeeCount: '',

        // 거래처 목록 (치과)
        partnerClinics: [],

        // 서비스 업종 공통
        serviceRegions: [],

        // 장비 A/S 전용
        asEquipmentTypes: [],
        asVisitFee: '',
        asResponseTime: '',

        // 교육/세미나 전용
        educationFields: [],
        educationType: '', // online, offline, both
        instructorInfo: '',

        // 청소/폐기물 전용
        cleaningServices: [],
        contractType: '', // monthly, yearly, per_visit

        // 금융 전용
        financeProducts: [],
        financeLicense: '',

        // 인테리어/개원 전용
        interiorSpecialties: [],
        portfolioUrl: '',
        projectCount: ''
    });

    // 업종별 사업 분야 옵션
    const getBusinessTypes = () => {
        switch (formData.vendorType) {
            case 'material_supplier':
                return ['치과 재료', '치과 장비', '기공 재료', '임플란트', '교정 재료', '소모품', '기타'];
            case 'equipment_as':
                return ['치과 장비 전문', '기공 장비 전문', '영상 장비 전문', '종합 A/S', '기타'];
            case 'education':
                return ['임상 교육', '기공 교육', '경영/마케팅', '디지털 치의학', '기타'];
            case 'cleaning':
                return ['의료폐기물 처리', '치과 청소 전문', '종합 청소', '멸균/소독', '기타'];
            case 'finance':
                return ['치과 전문 대출', '장비 리스', '보험', '투자/컨설팅', '기타'];
            case 'interior':
                return ['치과 인테리어', '개원 컨설팅', '리모델링', '종합 서비스', '기타'];
            default:
                return ['기타'];
        }
    };

    // 업종별 카테고리 옵션
    const getCategories = () => {
        switch (formData.vendorType) {
            case 'material_supplier':
                return [
                    '진료 장비', '임플란트', '교정 재료', '보철 재료', '근관 치료 재료',
                    '접착제/시멘트', '인상 재료', '의료용 소모품', '마스크/장갑', '소독/멸균 용품',
                    '기공 장비', '기공 재료', '치과용 의자', '엑스레이 장비', '구강 카메라', '레이저 장비', '기타'
                ];
            case 'equipment_as':
                return [
                    '치과 유니트체어', 'CT/X-ray', '구강스캐너', '밀링기', '3D프린터',
                    '소독기/멸균기', '콤프레셔', '석션', '핸드피스', '레이저 장비',
                    '기공 장비', '캐드캠 장비', '기타'
                ];
            case 'education':
                return [
                    '임플란트 시술', '심미 보철', '교정', '근관 치료', '소아 치과',
                    'CAD/CAM', '3D 프린팅', '디지털 워크플로우', '치과 경영',
                    '마케팅/홍보', '직원 교육', '기타'
                ];
            case 'cleaning':
                return [
                    '의료폐기물 수거', '감염성 폐기물', '일반 의료폐기물', '정기 청소',
                    '입주 청소', '카펫/바닥 청소', '에어컨 청소', '소독 서비스', '기타'
                ];
            case 'finance':
                return [
                    '개원 자금', '운영 자금', '장비 구입', '인테리어 대출',
                    '장비 리스', '의료기기 렌탈', '치과 전용 보험', '세무/회계 서비스', '기타'
                ];
            case 'interior':
                return [
                    '신규 개원', '리모델링', '확장 공사', '인테리어 설계',
                    '가구/집기', '사인물/간판', '개원 컨설팅', '입지 분석', '인허가 대행', '기타'
                ];
            default:
                return ['기타'];
        }
    };

    // 서비스 지역 옵션
    const serviceRegionOptions = [
        '서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종',
        '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주', '전국'
    ];

    // 기존 신청서 확인
    const checkExistingApplication = useCallback(async () => {
        try {
            const q = query(
                collection(db, 'sellerApplications'),
                where('userId', '==', userInfo.uid)
            );
            const snapshot = await getDocs(q);
            
            if (!snapshot.empty) {
                setHasExistingApplication(true);
                const existingApp = snapshot.docs[0].data();
                
                // 대기중이거나 거부된 경우만 표시
                if (existingApp.status === 'pending') {
                    alert('이미 신청서가 제출되어 승인 대기 중입니다.');
                    if (onBack) onBack();
                } else if (existingApp.status === 'rejected') {
                    alert('이전 신청이 거부되었습니다. 새로운 신청서를 작성해주세요.');
                    setHasExistingApplication(false);
                }
            }
        } catch (error) {
            console.error('신청서 확인 실패:', error);
        }
    }, [userInfo.uid, onBack]);

    useEffect(() => {
        checkExistingApplication();
    }, [checkExistingApplication]);

    // 입력 값 변경
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // 카테고리 선택 토글
    const toggleCategory = (category) => {
        setFormData(prev => {
            const categories = prev.productCategories.includes(category)
                ? prev.productCategories.filter(c => c !== category)
                : [...prev.productCategories, category];
            return { ...prev, productCategories: categories };
        });
    };

    // 서비스 지역 토글
    const toggleRegion = (region) => {
        setFormData(prev => {
            const regions = prev.serviceRegions.includes(region)
                ? prev.serviceRegions.filter(r => r !== region)
                : [...prev.serviceRegions, region];
            return { ...prev, serviceRegions: regions };
        });
    };

    // 업종 선택
    const selectVendorType = (typeId) => {
        setFormData(prev => ({ ...prev, vendorType: typeId, businessType: '', productCategories: [] }));
        setStep(1);
    };

    // 현재 업종 정보
    const getCurrentVendorType = () => {
        return vendorTypes.find(v => v.id === formData.vendorType);
    };

    // 서비스 업종인지 확인 (재료마켓 제외)
    const isServiceVendor = () => {
        return formData.vendorType && formData.vendorType !== 'material_supplier';
    };

    // 거래처 추가
    const addPartner = () => {
        if (!newPartner.name) {
            setError('거래처명을 입력해주세요.');
            return;
        }
        setFormData(prev => ({
            ...prev,
            partnerClinics: [...prev.partnerClinics, { ...newPartner, id: Date.now() }]
        }));
        setNewPartner({ name: '', phone: '', email: '' });
        setError('');
    };

    // 거래처 삭제
    const removePartner = (id) => {
        setFormData(prev => ({
            ...prev,
            partnerClinics: prev.partnerClinics.filter(p => p.id !== id)
        }));
    };

    // 다음 단계
    const handleNext = () => {
        setError('');

        if (step === 1) {
            // 기본 정보 유효성 검사
            if (!formData.companyName || !formData.businessNumber || !formData.ownerName) {
                setError('필수 항목을 모두 입력해주세요.');
                return;
            }
            if (!formData.contactPhone || !formData.contactEmail) {
                setError('연락처를 입력해주세요.');
                return;
            }
        } else if (step === 2) {
            // 카테고리 유효성 검사
            if (!formData.businessType) {
                setError('사업 분야를 선택해주세요.');
                return;
            }
            if (formData.productCategories.length === 0) {
                setError('서비스/판매 카테고리를 최소 1개 이상 선택해주세요.');
                return;
            }
            // 서비스 업종은 서비스 지역 필수
            if (isServiceVendor() && formData.serviceRegions.length === 0) {
                setError('서비스 가능 지역을 최소 1개 이상 선택해주세요.');
                return;
            }
        }
        // step 3 (거래처)은 선택사항이므로 유효성 검사 없음

        setStep(step + 1);
    };

    // 이전 단계
    const handlePrev = () => {
        setStep(step - 1);
    };

    // 신청서 제출
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // 유효성 검사
        if (!formData.description || formData.description.length < 50) {
            setError('회사 소개는 최소 50자 이상 입력해주세요.');
            return;
        }

        setLoading(true);

        try {
            // sellerApplications 컬렉션에 저장
            const applicationData = {
                userId: userInfo.uid,
                vendorType: formData.vendorType,
                companyName: formData.companyName,
                businessNumber: formData.businessNumber,
                ownerName: formData.ownerName,
                contactEmail: formData.contactEmail,
                contactPhone: formData.contactPhone,
                address: formData.address,
                detailAddress: formData.detailAddress || '',
                businessType: formData.businessType,
                productCategories: formData.productCategories,
                description: formData.description,
                website: formData.website || '',
                employeeCount: formData.employeeCount || '',
                partnerClinics: formData.partnerClinics || [],
                status: 'pending',
                appliedAt: new Date(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // 서비스 업종 공통 필드
            if (isServiceVendor()) {
                applicationData.serviceRegions = formData.serviceRegions || [];
            }

            // 업종별 추가 필드
            switch (formData.vendorType) {
                case 'equipment_as':
                    applicationData.asEquipmentTypes = formData.asEquipmentTypes || [];
                    applicationData.asVisitFee = formData.asVisitFee || '';
                    applicationData.asResponseTime = formData.asResponseTime || '';
                    break;
                case 'education':
                    applicationData.educationFields = formData.educationFields || [];
                    applicationData.educationType = formData.educationType || '';
                    applicationData.instructorInfo = formData.instructorInfo || '';
                    break;
                case 'cleaning':
                    applicationData.cleaningServices = formData.cleaningServices || [];
                    applicationData.contractType = formData.contractType || '';
                    break;
                case 'finance':
                    applicationData.financeProducts = formData.financeProducts || [];
                    applicationData.financeLicense = formData.financeLicense || '';
                    break;
                case 'interior':
                    applicationData.interiorSpecialties = formData.interiorSpecialties || [];
                    applicationData.portfolioUrl = formData.portfolioUrl || '';
                    applicationData.projectCount = formData.projectCount || '';
                    break;
                default:
                    break;
            }

            await addDoc(collection(db, 'sellerApplications'), applicationData);

            // users 컬렉션 업데이트
            await updateDoc(doc(db, 'users', userInfo.uid), {
                sellerApplicationSubmitted: true,
                sellerApplicationSubmittedAt: new Date().toISOString(),
                vendorType: formData.vendorType,
                updatedAt: new Date().toISOString()
            });

            alert('✅ 신청서가 제출되었습니다!\n관리자 승인 후 이용하실 수 있습니다.');
            
            // 신청 현황 페이지로 이동
            window.location.href = '/seller-application-status';

        } catch (error) {
            console.error('신청서 제출 실패:', error);
            setError('신청서 제출에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.box}>
                {/* 혜택 배너 */}
                <div style={styles.benefitBanner}>
                    <Gift size={20} />
                    <span><strong>무료 입점!</strong> 입점비·수수료 없이 바로 판매 시작하세요</span>
                </div>

                {/* 헤더 */}
                <div style={styles.header}>
                    {getCurrentVendorType() ? (
                        <>
                            {React.createElement(getCurrentVendorType().icon, { size: 40, style: { color: getCurrentVendorType().color } })}
                            <h1 style={styles.title}>{getCurrentVendorType().label} 입점 신청</h1>
                            <p style={styles.subtitle}>{getCurrentVendorType().desc}</p>
                        </>
                    ) : (
                        <>
                            <Store size={40} style={{ color: '#8b5cf6' }} />
                            <h1 style={styles.title}>파트너 입점 신청</h1>
                            <p style={styles.subtitle}>
                                덴탈클러스터에서 새로운 고객을 만나보세요
                            </p>
                        </>
                    )}

                    {/* 진행 단계 - 5단계 (업종선택 포함) */}
                    {step > 0 && (
                        <div style={styles.stepper}>
                            <div style={{ ...styles.step, ...(step >= 1 ? styles.stepActive : {}) }}>
                                <div style={{...styles.stepNumber, backgroundColor: step >= 1 ? (getCurrentVendorType()?.color || '#8b5cf6') : '#e2e8f0', color: step >= 1 ? '#fff' : '#94a3b8'}}>1</div>
                                <div style={styles.stepLabel}>기본정보</div>
                            </div>
                            <div style={styles.stepLine}></div>
                            <div style={{ ...styles.step, ...(step >= 2 ? styles.stepActive : {}) }}>
                                <div style={{...styles.stepNumber, backgroundColor: step >= 2 ? (getCurrentVendorType()?.color || '#8b5cf6') : '#e2e8f0', color: step >= 2 ? '#fff' : '#94a3b8'}}>2</div>
                                <div style={styles.stepLabel}>{isServiceVendor() ? '서비스' : '카테고리'}</div>
                            </div>
                            <div style={styles.stepLine}></div>
                            <div style={{ ...styles.step, ...(step >= 3 ? styles.stepActive : {}) }}>
                                <div style={{...styles.stepNumber, backgroundColor: step >= 3 ? (getCurrentVendorType()?.color || '#8b5cf6') : '#e2e8f0', color: step >= 3 ? '#fff' : '#94a3b8'}}>3</div>
                                <div style={styles.stepLabel}>{isServiceVendor() ? '추가정보' : '거래처'}</div>
                            </div>
                            <div style={styles.stepLine}></div>
                            <div style={{ ...styles.step, ...(step >= 4 ? styles.stepActive : {}) }}>
                                <div style={{...styles.stepNumber, backgroundColor: step >= 4 ? (getCurrentVendorType()?.color || '#8b5cf6') : '#e2e8f0', color: step >= 4 ? '#fff' : '#94a3b8'}}>4</div>
                                <div style={styles.stepLabel}>회사소개</div>
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    {/* 에러 메시지 */}
                    {error && (
                        <div style={styles.error}>
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {/* Step 0: 업종 선택 */}
                    {step === 0 && (
                        <div style={styles.stepContent}>
                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <Store size={20} />
                                    입점할 업종을 선택해주세요
                                </h3>
                                <p style={styles.description}>
                                    입점하려는 서비스 분야를 선택하시면 해당 업종에 맞는 신청서가 제공됩니다.
                                </p>

                                <div style={styles.vendorTypeGrid}>
                                    {vendorTypes.map(type => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            onClick={() => selectVendorType(type.id)}
                                            style={styles.vendorTypeCard}
                                        >
                                            <div style={{...styles.vendorTypeIcon, backgroundColor: `${type.color}15`}}>
                                                {React.createElement(type.icon, { size: 32, color: type.color })}
                                            </div>
                                            <h4 style={{...styles.vendorTypeName, color: type.color}}>{type.label}</h4>
                                            <p style={styles.vendorTypeDesc}>{type.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 1: 기본 정보 */}
                    {step === 1 && (
                        <div style={styles.stepContent}>
                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <Building size={20} />
                                    회사 기본 정보
                                </h3>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>회사명 *</label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder="(주)○○메디컬"
                                        style={styles.input}
                                        required
                                    />
                                </div>

                                <div style={styles.row}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>사업자등록번호 *</label>
                                        <input
                                            type="text"
                                            name="businessNumber"
                                            value={formData.businessNumber}
                                            onChange={handleChange}
                                            placeholder="000-00-00000"
                                            style={styles.input}
                                            required
                                        />
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>대표자명 *</label>
                                        <input
                                            type="text"
                                            name="ownerName"
                                            value={formData.ownerName}
                                            onChange={handleChange}
                                            placeholder="홍길동"
                                            style={styles.input}
                                            required
                                        />
                                    </div>
                                </div>

                                <div style={styles.row}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>직원 수</label>
                                        <input
                                            type="text"
                                            name="employeeCount"
                                            value={formData.employeeCount}
                                            onChange={handleChange}
                                            placeholder="예: 10명"
                                            style={styles.input}
                                        />
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>웹사이트</label>
                                        <input
                                            type="url"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            placeholder="https://www.example.com"
                                            style={styles.input}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <Phone size={20} />
                                    연락처 정보
                                </h3>

                                <div style={styles.row}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>전화번호 *</label>
                                        <input
                                            type="tel"
                                            name="contactPhone"
                                            value={formData.contactPhone}
                                            onChange={handleChange}
                                            placeholder="02-1234-5678"
                                            style={styles.input}
                                            required
                                        />
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>이메일 *</label>
                                        <input
                                            type="email"
                                            name="contactEmail"
                                            value={formData.contactEmail}
                                            onChange={handleChange}
                                            placeholder="contact@example.com"
                                            style={styles.input}
                                            required
                                        />
                                    </div>
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>주소</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        placeholder="서울시 강남구 테헤란로 123"
                                        style={styles.input}
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>상세주소</label>
                                    <input
                                        type="text"
                                        name="detailAddress"
                                        value={formData.detailAddress}
                                        onChange={handleChange}
                                        placeholder="○○빌딩 5층"
                                        style={styles.input}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: 사업 분야 & 카테고리 & 서비스 지역 */}
                    {step === 2 && (
                        <div style={styles.stepContent}>
                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <Award size={20} />
                                    사업 분야 *
                                </h3>
                                <p style={styles.description}>
                                    주력 사업 분야를 선택해주세요
                                </p>

                                <div style={styles.radioGroup}>
                                    {getBusinessTypes().map(type => (
                                        <label key={type} style={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="businessType"
                                                value={type}
                                                checked={formData.businessType === type}
                                                onChange={handleChange}
                                                style={styles.radio}
                                            />
                                            <span>{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <Package size={20} />
                                    {isServiceVendor() ? '서비스 카테고리 *' : '판매 카테고리 *'}
                                </h3>
                                <p style={styles.description}>
                                    {isServiceVendor() ? '제공하실 서비스를 선택해주세요 (복수 선택 가능)' : '판매하실 제품 카테고리를 선택해주세요 (복수 선택 가능)'}
                                </p>

                                <div style={styles.categoryGrid}>
                                    {getCategories().map(category => (
                                        <button
                                            key={category}
                                            type="button"
                                            onClick={() => toggleCategory(category)}
                                            style={{
                                                ...styles.categoryBtn,
                                                ...(formData.productCategories.includes(category)
                                                    ? {...styles.categoryBtnActive, borderColor: getCurrentVendorType()?.color, backgroundColor: `${getCurrentVendorType()?.color}10`, color: getCurrentVendorType()?.color}
                                                    : {})
                                            }}
                                        >
                                            {formData.productCategories.includes(category) && (
                                                <Check size={16} />
                                            )}
                                            {category}
                                        </button>
                                    ))}
                                </div>

                                {formData.productCategories.length > 0 && (
                                    <div style={{...styles.selectedCount, backgroundColor: `${getCurrentVendorType()?.color}10`, color: getCurrentVendorType()?.color}}>
                                        선택된 카테고리: {formData.productCategories.length}개
                                    </div>
                                )}
                            </div>

                            {/* 서비스 업종: 서비스 가능 지역 */}
                            {isServiceVendor() && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <MapPin size={20} />
                                        서비스 가능 지역 *
                                    </h3>
                                    <p style={styles.description}>
                                        서비스 제공이 가능한 지역을 선택해주세요 (복수 선택 가능)
                                    </p>

                                    <div style={styles.regionGrid}>
                                        {serviceRegionOptions.map(region => (
                                            <button
                                                key={region}
                                                type="button"
                                                onClick={() => toggleRegion(region)}
                                                style={{
                                                    ...styles.regionBtn,
                                                    ...(formData.serviceRegions.includes(region)
                                                        ? {...styles.regionBtnActive, borderColor: getCurrentVendorType()?.color, backgroundColor: getCurrentVendorType()?.color, color: '#fff'}
                                                        : {})
                                                }}
                                            >
                                                {region}
                                            </button>
                                        ))}
                                    </div>

                                    {formData.serviceRegions.length > 0 && (
                                        <div style={{...styles.selectedCount, backgroundColor: `${getCurrentVendorType()?.color}10`, color: getCurrentVendorType()?.color}}>
                                            선택된 지역: {formData.serviceRegions.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: 업종별 추가 정보 */}
                    {step === 3 && (
                        <div style={styles.stepContent}>
                            {/* 재료마켓: 거래처 등록 */}
                            {formData.vendorType === 'material_supplier' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <Users size={20} />
                                        거래처 등록 (선택)
                                    </h3>
                                    <p style={styles.description}>
                                        현재 거래 중인 치과를 등록하면, 승인 후 해당 치과에 앱 홍보 안내를 보낼 수 있습니다.
                                        <br />나중에 추가할 수도 있습니다.
                                    </p>

                                    {/* 거래처 입력 폼 */}
                                    <div style={styles.partnerInputBox}>
                                        <input
                                            type="text"
                                            placeholder="치과명 *"
                                            value={newPartner.name}
                                            onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                                            style={styles.partnerInput}
                                        />
                                        <input
                                            type="tel"
                                            placeholder="연락처"
                                            value={newPartner.phone}
                                            onChange={(e) => setNewPartner({ ...newPartner, phone: e.target.value })}
                                            style={styles.partnerInput}
                                        />
                                        <input
                                            type="email"
                                            placeholder="이메일"
                                            value={newPartner.email}
                                            onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                                            style={styles.partnerInput}
                                        />
                                        <button type="button" onClick={addPartner} style={{...styles.addPartnerBtn, backgroundColor: getCurrentVendorType()?.color}}>
                                            <Plus size={18} /> 추가
                                        </button>
                                    </div>

                                    {/* 등록된 거래처 목록 */}
                                    {formData.partnerClinics.length > 0 && (
                                        <div style={styles.partnerList}>
                                            <h4 style={styles.partnerListTitle}>등록된 거래처 ({formData.partnerClinics.length}곳)</h4>
                                            {formData.partnerClinics.map((partner) => (
                                                <div key={partner.id} style={styles.partnerItem}>
                                                    <div style={styles.partnerInfo}>
                                                        <strong>{partner.name}</strong>
                                                        {partner.phone && <span> · {partner.phone}</span>}
                                                        {partner.email && <span> · {partner.email}</span>}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removePartner(partner.id)}
                                                        style={styles.removePartnerBtn}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {formData.partnerClinics.length === 0 && (
                                        <div style={styles.emptyPartners}>
                                            거래처를 등록하지 않아도 신청이 가능합니다
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 장비 A/S: 추가 정보 */}
                            {formData.vendorType === 'equipment_as' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <Wrench size={20} />
                                        A/S 서비스 정보
                                    </h3>
                                    <p style={styles.description}>
                                        A/S 서비스에 대한 상세 정보를 입력해주세요
                                    </p>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>출장비 안내</label>
                                        <input
                                            type="text"
                                            name="asVisitFee"
                                            value={formData.asVisitFee}
                                            onChange={handleChange}
                                            placeholder="예: 서울/경기 무료, 지방 3만원~"
                                            style={styles.input}
                                        />
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>평균 대응 시간</label>
                                        <select
                                            name="asResponseTime"
                                            value={formData.asResponseTime}
                                            onChange={handleChange}
                                            style={styles.input}
                                        >
                                            <option value="">선택해주세요</option>
                                            <option value="same_day">당일 방문</option>
                                            <option value="next_day">익일 방문</option>
                                            <option value="2_3_days">2~3일 내</option>
                                            <option value="within_week">1주일 내</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* 교육/세미나: 추가 정보 */}
                            {formData.vendorType === 'education' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <GraduationCap size={20} />
                                        교육 서비스 정보
                                    </h3>
                                    <p style={styles.description}>
                                        교육/세미나 서비스에 대한 상세 정보를 입력해주세요
                                    </p>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>교육 진행 방식</label>
                                        <div style={styles.radioGroup}>
                                            {[
                                                { value: 'offline', label: '오프라인' },
                                                { value: 'online', label: '온라인' },
                                                { value: 'both', label: '온/오프라인 병행' }
                                            ].map(opt => (
                                                <label key={opt.value} style={styles.radioLabel}>
                                                    <input
                                                        type="radio"
                                                        name="educationType"
                                                        value={opt.value}
                                                        checked={formData.educationType === opt.value}
                                                        onChange={handleChange}
                                                        style={styles.radio}
                                                    />
                                                    <span>{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>대표 강사 소개</label>
                                        <textarea
                                            name="instructorInfo"
                                            value={formData.instructorInfo}
                                            onChange={handleChange}
                                            placeholder="대표 강사의 경력, 전문 분야 등을 소개해주세요"
                                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 청소/폐기물: 추가 정보 */}
                            {formData.vendorType === 'cleaning' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <Trash2 size={20} />
                                        청소/폐기물 서비스 정보
                                    </h3>
                                    <p style={styles.description}>
                                        서비스에 대한 상세 정보를 입력해주세요
                                    </p>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>계약 방식</label>
                                        <div style={styles.radioGroup}>
                                            {[
                                                { value: 'monthly', label: '월 정기 계약' },
                                                { value: 'yearly', label: '연간 계약' },
                                                { value: 'per_visit', label: '건별 계약' },
                                                { value: 'flexible', label: '협의 가능' }
                                            ].map(opt => (
                                                <label key={opt.value} style={styles.radioLabel}>
                                                    <input
                                                        type="radio"
                                                        name="contractType"
                                                        value={opt.value}
                                                        checked={formData.contractType === opt.value}
                                                        onChange={handleChange}
                                                        style={styles.radio}
                                                    />
                                                    <span>{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 금융: 추가 정보 */}
                            {formData.vendorType === 'finance' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <Banknote size={20} />
                                        금융 서비스 정보
                                    </h3>
                                    <p style={styles.description}>
                                        금융 서비스에 대한 상세 정보를 입력해주세요
                                    </p>

                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>금융업 등록/허가 정보</label>
                                        <input
                                            type="text"
                                            name="financeLicense"
                                            value={formData.financeLicense}
                                            onChange={handleChange}
                                            placeholder="예: 대부업 등록번호, 보험대리점 등록번호 등"
                                            style={styles.input}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 인테리어/개원: 추가 정보 */}
                            {formData.vendorType === 'interior' && (
                                <div style={styles.section}>
                                    <h3 style={styles.sectionTitle}>
                                        <Home size={20} />
                                        인테리어/개원 서비스 정보
                                    </h3>
                                    <p style={styles.description}>
                                        서비스에 대한 상세 정보를 입력해주세요
                                    </p>

                                    <div style={styles.row}>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>포트폴리오 URL</label>
                                            <input
                                                type="url"
                                                name="portfolioUrl"
                                                value={formData.portfolioUrl}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                                style={styles.input}
                                            />
                                        </div>

                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>시공 실적</label>
                                            <input
                                                type="text"
                                                name="projectCount"
                                                value={formData.projectCount}
                                                onChange={handleChange}
                                                placeholder="예: 치과 50건 이상"
                                                style={styles.input}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 4: 회사 소개 */}
                    {step === 4 && (
                        <div style={styles.stepContent}>
                            <div style={styles.section}>
                                <h3 style={styles.sectionTitle}>
                                    <FileText size={20} />
                                    회사 소개 *
                                </h3>
                                <p style={styles.description}>
                                    회사의 주요 제품, 서비스, 강점 등을 자세히 설명해주세요 (최소 50자)
                                </p>

                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="예시:
저희 회사는 치과용 임플란트 전문 공급업체로, 국내외 유명 브랜드 제품을 취급하고 있습니다.
15년 이상의 업력을 바탕으로 전국 500여개 치과에 제품을 공급하고 있으며,
신속한 배송과 전문적인 A/S를 제공합니다.

주요 취급 제품:
- 임플란트 시스템
- 보철 재료
- 수술 기구
..."
                                    style={styles.textarea}
                                    rows="12"
                                    required
                                />

                                <div style={styles.charCount}>
                                    {formData.description.length} / 최소 50자
                                </div>
                            </div>

                            {/* 제출 전 확인 사항 */}
                            <div style={styles.confirmBox}>
                                <h4 style={styles.confirmTitle}>📋 제출 전 확인사항</h4>
                                <ul style={styles.confirmList}>
                                    <li>입력하신 정보가 정확한지 확인해주세요</li>
                                    <li>사업자등록번호는 실제 등록된 번호여야 합니다</li>
                                    <li>관리자 승인 후 판매자 기능을 이용하실 수 있습니다</li>
                                    <li>허위 정보 작성 시 승인이 거부될 수 있습니다</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* 버튼 */}
                    {step > 0 && (
                        <div style={styles.buttonGroup}>
                            {step > 1 && (
                                <button
                                    type="button"
                                    onClick={handlePrev}
                                    style={styles.prevButton}
                                    disabled={loading}
                                >
                                    <ArrowLeft size={20} />
                                    이전
                                </button>
                            )}

                            {step < 4 ? (
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    style={{...styles.nextButton, backgroundColor: getCurrentVendorType()?.color || '#8b5cf6'}}
                                >
                                    다음
                                    <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    style={styles.submitButton}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                                            제출 중...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={20} />
                                            신청서 제출
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </form>

                {/* 취소 버튼 */}
                {onBack && (
                    <div style={styles.footer}>
                        <button
                            onClick={onBack}
                            style={styles.cancelButton}
                            type="button"
                        >
                            취소
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: '40px 20px',
    },
    box: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '40px',
        maxWidth: '800px',
        margin: '0 auto',
    },
    // 업종 선택 그리드
    vendorTypeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
        marginTop: '20px',
    },
    vendorTypeCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 16px',
        backgroundColor: '#ffffff',
        border: '2px solid #e2e8f0',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
    },
    vendorTypeIcon: {
        width: '64px',
        height: '64px',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '12px',
    },
    vendorTypeName: {
        margin: '0 0 6px 0',
        fontSize: '16px',
        fontWeight: '700',
    },
    vendorTypeDesc: {
        margin: 0,
        fontSize: '12px',
        color: '#64748b',
        lineHeight: '1.4',
    },
    // 서비스 지역 그리드
    regionGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
    },
    regionBtn: {
        padding: '10px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        color: '#475569',
        transition: 'all 0.2s',
    },
    regionBtnActive: {
        borderColor: '#8b5cf6',
        backgroundColor: '#8b5cf6',
        color: '#ffffff',
    },
    benefitBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '14px 20px',
        backgroundColor: '#fef3c7',
        borderRadius: '10px',
        marginBottom: '24px',
        fontSize: '15px',
        color: '#92400e',
    },
    header: {
        textAlign: 'center',
        marginBottom: '40px',
    },
    title: {
        margin: '16px 0 8px 0',
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
    },
    subtitle: {
        margin: '0 0 32px 0',
        fontSize: '14px',
        color: '#64748b',
    },
    stepper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '32px',
    },
    step: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
    },
    stepNumber: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '600',
        fontSize: '16px',
    },
    stepActive: {
        '& > div:first-child': {
            backgroundColor: '#8b5cf6',
            color: '#ffffff',
        },
    },
    stepLabel: {
        fontSize: '12px',
        color: '#64748b',
        fontWeight: '500',
    },
    stepLine: {
        width: '60px',
        height: '2px',
        backgroundColor: '#e2e8f0',
        marginBottom: '24px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    description: {
        margin: 0,
        fontSize: '14px',
        color: '#64748b',
        lineHeight: '1.6',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    input: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        transition: 'all 0.2s',
    },
    textarea: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        fontFamily: 'inherit',
        lineHeight: '1.6',
        resize: 'vertical',
    },
    charCount: {
        textAlign: 'right',
        fontSize: '12px',
        color: '#94a3b8',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    radio: {
        width: '18px',
        height: '18px',
        cursor: 'pointer',
    },
    categoryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '12px',
    },
    categoryBtn: {
        padding: '12px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        color: '#475569',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s',
    },
    categoryBtnActive: {
        borderColor: '#8b5cf6',
        backgroundColor: '#f5f3ff',
        color: '#8b5cf6',
    },
    selectedCount: {
        textAlign: 'center',
        fontSize: '14px',
        color: '#8b5cf6',
        fontWeight: '600',
        padding: '8px',
        backgroundColor: '#f5f3ff',
        borderRadius: '8px',
    },
    confirmBox: {
        padding: '20px',
        backgroundColor: '#f1f5f9',
        borderRadius: '8px',
        border: '1px solid #cbd5e1',
    },
    confirmTitle: {
        margin: '0 0 12px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    confirmList: {
        margin: 0,
        paddingLeft: '20px',
        color: '#475569',
        fontSize: '14px',
        lineHeight: '2',
    },
    error: {
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#dc2626',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    buttonGroup: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'space-between',
        marginTop: '16px',
    },
    prevButton: {
        padding: '14px 24px',
        backgroundColor: '#ffffff',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s',
    },
    nextButton: {
        padding: '14px 24px',
        backgroundColor: '#8b5cf6',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    submitButton: {
        padding: '14px 24px',
        backgroundColor: '#10b981',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        flex: 1,
        transition: 'all 0.2s',
    },
    footer: {
        marginTop: '24px',
        textAlign: 'center',
    },
    cancelButton: {
        background: 'none',
        border: 'none',
        color: '#64748b',
        cursor: 'pointer',
        fontSize: '14px',
        textDecoration: 'underline',
    },
    // 거래처 등록 스타일
    partnerInputBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '16px',
    },
    partnerInput: {
        flex: '1 1 150px',
        padding: '12px 14px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
    },
    addPartnerBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '12px 20px',
        backgroundColor: '#8b5cf6',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    partnerList: {
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        padding: '16px',
    },
    partnerListTitle: {
        margin: '0 0 12px 0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
    },
    partnerItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        marginBottom: '8px',
        border: '1px solid #e2e8f0',
    },
    partnerInfo: {
        fontSize: '14px',
        color: '#1e293b',
    },
    removePartnerBtn: {
        padding: '6px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyPartners: {
        textAlign: 'center',
        padding: '30px 20px',
        color: '#94a3b8',
        fontSize: '14px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
    },
};

export default SellerApplication;